import { Button as BaseButton } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn, withBase } from "./cn";

// Paper: ui/Button/* on "00 Design system" and the 12x dialogs. One primary per view.
export const buttonVariants = cva(
  [
    "inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium tracking-ui",
    "transition-[background-color,box-shadow,color,filter,transform] duration-150 ease-out active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-50 data-disabled:pointer-events-none data-disabled:opacity-50",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-linear-to-b from-accent-top to-accent text-white shadow-button-primary hover:brightness-110 [&_svg]:text-white",
        secondary:
          "bg-linear-to-b from-control to-surface-3 text-text shadow-button hover:from-[#2d2d2d] hover:to-control",
        ghost: "text-text-2 hover:bg-surface-3 hover:text-text",
        light: "bg-linear-to-b from-[#f2f2f2] to-light text-on-light shadow-button-light hover:from-white",
        danger:
          "bg-linear-to-b from-negative to-negative-deep text-white shadow-button-danger hover:brightness-110",
      },
      size: {
        md: "h-[34px] px-3.5 text-[14px] leading-[18px] [&_svg]:size-4",
        sm: "h-[30px] px-3 text-[13px] leading-4 [&_svg]:size-[15px]",
        lg: "h-9 px-4 text-[14px] leading-[18px] [&_svg]:size-4",
        // Sign in and the setup steps: the full screen flows use 40 tall controls.
        xl: "h-10 px-5 text-[14px] leading-[18px] [&_svg]:size-4",
        icon: "size-8 rounded-[9px] [&_svg]:size-[18px]",
        "icon-sm": "size-[30px] rounded-row [&_svg]:size-4",
        // The composer's send and stop: a 40px circle inside its 56px pill.
        round: "size-10 rounded-full [&_svg]:size-[18px]",
      },
    },
    compoundVariants: [
      { variant: "primary", size: "md", className: "px-4" },
      { variant: "danger", size: "md", className: "px-4" },
      { variant: "ghost", size: "icon", className: "text-icon" },
      { variant: "ghost", size: "icon-sm", className: "text-icon" },
    ],
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export type ButtonProps = ComponentProps<typeof BaseButton> & VariantProps<typeof buttonVariants>;

// A Button rendered as something else (a router Link) is not a native <button>; Base UI
// needs to know so it adds the button role and keyboard handling itself.
export const Button = ({ variant, size, className, ...props }: ButtonProps) => (
  <BaseButton
    className={withBase(buttonVariants({ variant, size }), className)}
    nativeButton={props.render === undefined}
    {...props}
  />
);

/** Keyboard hint chip. `onAccent` for chips sitting on the primary button. */
export const Kbd = ({ onAccent = false, className, ...props }: ComponentProps<"kbd"> & { onAccent?: boolean }) => (
  <kbd
    className={cn(
      "inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-sm px-1.5 font-sans text-[11px] font-medium leading-none tracking-normal",
      onAccent
        ? "bg-white/8 text-white/85 shadow-[inset_0_0_0_1px_#ffffff33]"
        : "bg-surface-3 text-text-2 shadow-ring-inset",
      className,
    )}
    {...props}
  />
);
